import {
    alpha,
    Box,
    Drawer,
    List,
    ListItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    ListItemButton,
} from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    FooterLinksContainer,
    FooterLinkText,
    FooterTypography,
    SeparatorText,
} from '../../pages/registration/registration-styles';
import { RootState } from '../../store';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import { LinkTextWrapper } from '../global-styles/GlobalStyles';
import { colors } from '../theme/theme';

export interface SidebarRef {
    openDrawer: () => void;
    closeDrawer: () => void;
}

interface FooterLegalLinksProps {
    typographyVariant: 'caption' | 'overline';
    isImpersonating?: boolean;
    t: (key: string) => string;
}

const FooterLegalLinks: React.FC<FooterLegalLinksProps> = ({ typographyVariant, isImpersonating, t }) => (
    <FooterLinksContainer>
        <FooterTypography variant={typographyVariant}>
            <LinkTextWrapper
                sx={FooterLinkText}
                target={isImpersonating ? undefined : '_blank'}
                href="https://auxcap.com/terms-of-use/"
                onClick={isImpersonating ? e => e.preventDefault() : undefined}
            >
                {t('registration.links.termsOfUse')}
            </LinkTextWrapper>
            <SeparatorText variant="overline" color="textPrimary">
                {' | '}
            </SeparatorText>
            <LinkTextWrapper
                sx={FooterLinkText}
                target={isImpersonating ? undefined : '_blank'}
                href="https://auxcap.com/privacy-policy/"
                onClick={isImpersonating ? e => e.preventDefault() : undefined}
            >
                {t('registration.links.privacyPolicy')}
            </LinkTextWrapper>
        </FooterTypography>
    </FooterLinksContainer>
);

const AppVersionText: React.FC<{ appVersion: string; py?: number }> = ({ appVersion, py }) => (
    <Typography variant="caption" fontWeight={600} color="textSecondary" textAlign={'center'} py={py}>
        v{appVersion}
    </Typography>
);

export const Sidebar = React.forwardRef<SidebarRef, object>((_props, ref) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();

    // Clear pending path when location updates
    React.useEffect(() => {
        setPendingPath(null);
    }, [location.pathname]);

    React.useImperativeHandle(ref, () => ({
        openDrawer: () => setDrawerOpen(true),
        closeDrawer: () => setDrawerOpen(false),
    }));

    const menuItems = [
        { icon: 'icon-dashboard-card', name: 'Dashboard', path: NavigationConfig.Home },
        { icon: 'icon-contracts', name: 'Contracts', path: NavigationConfig.ContractOverview },
        { icon: 'icon-loan', name: 'Payments', path: NavigationConfig.Payments },
        { icon: 'icon-documents', name: 'Documents', path: NavigationConfig.DocumentOverview },
        { icon: 'icon-user-square', name: 'Banking', path: NavigationConfig.BankAccounts },
        { icon: 'icon-help', name: 'Support', path: NavigationConfig.Support },
        { icon: 'icon-settings', name: 'Settings', path: NavigationConfig.Settings },
    ];

    const appVersion = localStorage.getItem('APP_VERSION') || process.env.REACT_APP_APP_VERSION || '';

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleMenuItemClick = () => {
        setDrawerOpen(false);
    };

    const menuContent = (
        <List sx={{ py: 0 }}>
            {menuItems.map(item => {
                // Use pending path for optimistic UI, fallback to actual location
                const currentPath = pendingPath || location.pathname;
                const currentState = pendingPath ? null : location.state;

                // Custom active check for Contracts to include contract details page and make payment page when coming from contracts
                // Custom active check for Payments to include make payment page when coming from payments module
                const isMakePaymentPage = currentPath === NavigationConfig.DashboardMakePayment;
                const isFromContractsModule = currentState?.fromContracts === true;
                const isFromPaymentsModule = currentState?.fromPayments === true;
                const isFromDashboardModule = currentState?.fromDashboard === true;
                const isFromDocumentsModule = currentState?.fromDocuments === true;
                const isFromBankAccountsModule = currentState?.fromBankAccounts === true;

                const isActive =
                    item.path === NavigationConfig.ContractOverview
                        ? currentPath === NavigationConfig.ContractOverview ||
                          currentPath === NavigationConfig.ContractDetails ||
                          (isMakePaymentPage && isFromContractsModule)
                        : item.path === NavigationConfig.Payments
                          ? currentPath === NavigationConfig.Payments || (isMakePaymentPage && isFromPaymentsModule)
                          : item.path === NavigationConfig.Home
                            ? currentPath === NavigationConfig.Home || (isMakePaymentPage && isFromDashboardModule)
                            : item.path === NavigationConfig.DocumentOverview
                              ? currentPath === NavigationConfig.DocumentOverview ||
                                (isMakePaymentPage && isFromDocumentsModule)
                              : item.path === NavigationConfig.BankAccounts
                                ? currentPath === NavigationConfig.BankAccounts ||
                                  (isMakePaymentPage && isFromBankAccountsModule)
                                : currentPath === item.path;

                return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => {
                                setPendingPath(item.path);
                                handleMenuItemClick();
                                navigate(item.path);
                            }}
                            sx={{
                                ...theme.typography.body2,
                                padding: 1,
                                width: '100%',
                                borderRadius: 2,
                                transition: 'all 0.2s ease-in-out',

                                ...(isActive
                                    ? {
                                          color: colors.fullWhite,
                                          backgroundColor: colors.primaryLight[100],
                                          fontWeight: 600,
                                          '&:hover': {
                                              backgroundColor: colors.primaryLight[70],
                                          },
                                      }
                                    : {
                                          color: theme.palette.text.primary,
                                          backgroundColor: 'transparent',
                                          fontWeight: 400,
                                          '&:hover': {
                                              backgroundColor: alpha(colors.fullBlack, 0.05),
                                          },
                                      }),

                                '&:focus-visible': {
                                    outline: `2px solid ${colors.fullBlack}`,
                                    outlineOffset: '2px',
                                    color: colors.fullBlack,
                                },
                            }}
                        >
                            <Stack direction={'row'} sx={{ lineHeight: '21px', gap: 1, alignItems: 'center' }}>
                                <span
                                    className={item.icon}
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 400,
                                    }}
                                />
                                {item.name}
                            </Stack>
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );

    const drawerContent = (
        <Stack
            component="nav"
            sx={{
                width: '247px',
                backgroundColor: colors.primaryLight[5],
                height: '100%',
                p: 1.5,
                justifyContent: 'space-between',
            }}
        >
            {menuContent}
            <Stack gap={2}>
                <Box textAlign={'center'}>
                    <FooterLegalLinks
                        typographyVariant="overline"
                        isImpersonating={impersonation?.isImpersonating}
                        t={t}
                    />
                </Box>
                <AppVersionText appVersion={appVersion} />
            </Stack>
        </Stack>
    );

    if (!isMobile) {
        return (
            <>
                <Stack
                    component="nav"
                    sx={{
                        width: '247px',
                        backgroundColor: colors.primaryLight[5],
                        p: 1.5,
                        justifyContent: 'space-between',
                        border: `1px solid ${colors.greyPreset[20]}`,
                    }}
                >
                    {menuContent}
                    <Stack gap={1.5}>
                        <Stack textAlign={'center'}>
                            <FooterLegalLinks
                                typographyVariant="caption"
                                isImpersonating={impersonation?.isImpersonating}
                                t={t}
                            />
                        </Stack>
                        <AppVersionText appVersion={appVersion} py={1} />
                    </Stack>
                </Stack>
            </>
        );
    }

    // Mobile/Tablet drawer - only visible on mobile
    return (
        <>
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        backgroundColor: colors.greyPreset[90],
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
});

Sidebar.displayName = 'Sidebar';
